// src/contexts/CurrentUserProfileContext.js
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { updateUserProfile, updateCompleteProfile } from '../services/userService';
import { useAuth } from './AuthContext';

/**
 * Real-time profile synchronization with cache awareness.
 * Tracks both cache and server snapshots to prevent premature onboarding decisions.
 * @typedef {'idle'|'loading'|'exists'|'missing'|'error'} ProfileStatus
 */
const CurrentUserProfileContext = createContext(undefined);

const initialState = {
  status: 'idle',
  profile: null,
  error: null,
  hasReceivedSnapshot: false,
  hasServerSnapshot: false, // Sticky: once true, always true until sign out
  lastFromCache: false,
  hasPendingWrites: false,
  saving: false, // Persists across auth changes to prevent UI flicker
  saveError: null,
};

export const CurrentUserProfileProvider = ({ children }) => {
  const { status: authStatus, uid } = useAuth();
  const [state, setState] = useState(() => ({ ...initialState }));

  useEffect(() => {
    if (authStatus !== 'signedIn' || !uid) {
      setState(() => ({ ...initialState }));
      return undefined;
    }

    setState((prev) => ({
      ...initialState,
      status: 'loading',
      saving: prev.saving, // Preserve saving state during auth changes
      saveError: prev.saveError,
    }));

    const ref = doc(db, 'users', uid);
    const unsubscribe = onSnapshot(
      ref,
      { includeMetadataChanges: true },
      (snapshot) => {
        const exists = snapshot.exists();
        const data = exists ? { id: snapshot.id, ...snapshot.data() } : null;
        const fromCache = snapshot.metadata.fromCache;
        const hasPendingWrites = snapshot.metadata.hasPendingWrites;

        setState((prev) => ({
          ...prev,
          status: exists ? 'exists' : 'missing',
          profile: data,
          error: null,
          hasReceivedSnapshot: true,
          // Once we've seen server data, remember it (prevents cache-only decisions)
          hasServerSnapshot: prev.hasServerSnapshot || !fromCache,
          lastFromCache: fromCache,
          hasPendingWrites,
          saving: hasPendingWrites ? prev.saving : false,
          saveError: hasPendingWrites ? prev.saveError : null,
        }));
      },
      (error) => {
        console.error('Current user profile subscription error:', error);
        setState((prev) => ({
          ...prev,
          status: 'error',
          profile: null,
          error,
        }));
      }
    );

    return unsubscribe;
  }, [authStatus, uid]);

  const updatePartialProfile = useCallback(
    async (profileData) => {
      if (!uid) {
        throw new Error('User not authenticated');
      }

      setState((prev) => ({ ...prev, saving: true, saveError: null }));

      try {
        await updateUserProfile(profileData);
        setState((prev) => ({ ...prev, saving: false }));
      } catch (error) {
        setState((prev) => ({ ...prev, saving: false, saveError: error }));
        throw error;
      }
    },
    [uid]
  );

  const updateFullProfile = useCallback(
    async (profileData, originalUsername, profilePicture) => {
      if (!uid) {
        throw new Error('User not authenticated');
      }

      // Preserve first completion timestamp across subsequent edits
      const existingProfileCompleteAt = state.profile?.profileCompleteAt ?? null;

      setState((prev) => ({ ...prev, saving: true, saveError: null }));

      try {
        const result = await updateCompleteProfile(
          profileData,
          originalUsername,
          profilePicture,
          existingProfileCompleteAt
        );
        setState((prev) => ({ ...prev, saving: false }));
        return result;
      } catch (error) {
        setState((prev) => ({ ...prev, saving: false, saveError: error }));
        throw error;
      }
    },
    [uid, state.profile?.profileCompleteAt]
  );

  const value = useMemo(
    () => ({
      ...state,
      updateProfile: updatePartialProfile,
      updateCompleteUserProfile: updateFullProfile,
    }),
    [state, updatePartialProfile, updateFullProfile]
  );

  return (
    <CurrentUserProfileContext.Provider value={value}>
      {children}
    </CurrentUserProfileContext.Provider>
  );
};

/**
 * Hook to access current user's profile state and update methods.
 * @returns {Object} Profile state with status, profile data, and update methods
 */
export const useCurrentUserProfile = () => {
  const context = useContext(CurrentUserProfileContext);
  if (!context) {
    throw new Error('useCurrentUserProfile must be used within a CurrentUserProfileProvider');
  }
  return context;
};
